"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { ArrowLeft, Save, Loader2, Plus, X, Upload, Trash2 } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { Label } from "../../ui/label";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { Input } from "../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";

interface BorrIndASSubstantiveProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
  procedureId?: string;
}

interface Procedure {
  name: string;
  sectionName: string;
}

interface ProcedureResponse {
  id: string;
  textResponse?: { html: string; plainText: string } | null;
  documents?: Array<{
    id: string;
    type: "textbox" | "linked" | "uploaded";
    name: string;
    reference?: string;
    content?: string; // For textbox
    url?: string;
  }>;
}

type DebtType = "Debenture" | "Bonds" | "Term Loan" | "Other";
type InterestRateType = "Fixed" | "Floating";
type ValuationMethod = "Amortised Cost" | "FVTPL";

type TestingReferenceModuleId = "" | string;

interface DebtMasterRow {
  id: string;
  debtName: string;
  partyName: string;
  debtType: DebtType | "";
  interestRate: InterestRateType | "";
  agreementWpName: string; // linked existing WP (file name)
  agreementWpUrl?: string; // optional webUrl
  valuationMethod: ValuationMethod | "";
  testingReference: TestingReferenceModuleId;
  sourceKey?: string; // original key under loans[...] when loaded
  assessmentFileName?: string;
  assessmentFileUrl?: string;
}

const procedures: Procedure[] = [
  { name: "Dashboard & Agreement Summary", sectionName: "DashboardAgreementSummary" },
  { name: "Repayment testing", sectionName: "RepaymentTesting" },
  { name: "Covenant testing", sectionName: "CovenantTesting" },
  { name: "Drawing Power", sectionName: "DrawingPower" },
  { name: "Utilisation Testing", sectionName: "UtilisationTesting" },
  { name: "Quarterly Stock Statement", sectionName: "QuarterlyStockStatement" },
  { name: "Classification testing", sectionName: "ClassificationTesting" },
];

// Map procedureId to sectionName
const getSectionName = (procedureId?: string): string | null => {
  if (!procedureId) return null;
  const mapping: Record<string, string> = {
    "dashboard-agreement-summary": "DashboardAgreementSummary",
    "repayment-testing": "RepaymentTesting",
    "covenant-testing": "CovenantTesting",
    "drawing-power": "DrawingPower",
    "utilisation-testing": "UtilisationTesting",
    "quarterly-stock-statement": "QuarterlyStockStatement",
    "classification-testing": "ClassificationTesting",
  };
  return mapping[procedureId] || null;
};

export default function BorrIndASSubstantive({ onBack, setActiveSection, procedureId }: BorrIndASSubstantiveProps) {
  const [companyName, setCompanyName] = useState<string>("");
  const [areaPrefix, setAreaPrefix] = useState<string>("Bor");
  const [responses, setResponses] = useState<ProcedureResponse[]>([]);
  const [inferenceQuestion, setInferenceQuestion] = useState<ProcedureResponse>({
    id: "inference-conclusion",
    textResponse: null,
    documents: [],
  });
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  // Dashboard & Agreement Summary (Borrowings - Ind AS) debt master table state
  const [debtRows, setDebtRows] = useState<DebtMasterRow[]>([]);
  const [availableAgreements, setAvailableAgreements] = useState<Array<{ name: string; url?: string }>>([]);
  const [loanDetailsByKey, setLoanDetailsByKey] = useState<Record<string, any>>({});
  const [debtSaving, setDebtSaving] = useState(false);
  const [debtSaveStatus, setDebtSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [debtSaveError, setDebtSaveError] = useState<string | null>(null);
  const [debtTestRunning, setDebtTestRunning] = useState(false);
  const [debtTestError, setDebtTestError] = useState<string | null>(null);

  const assessmentFileInputRef = useRef<HTMLInputElement | null>(null);
  const [assessmentUploadTargetRowId, setAssessmentUploadTargetRowId] = useState<string | null>(null);
  const [assessmentUploadingRowId, setAssessmentUploadingRowId] = useState<string | null>(null);
  const [assessmentDeletingRowId, setAssessmentDeletingRowId] = useState<string | null>(null);

  // Open Module dialog state (per loan)
  const [moduleOpen, setModuleOpen] = useState(false);
  const [moduleLoanKey, setModuleLoanKey] = useState<string>("");
  const [moduleRowId, setModuleRowId] = useState<string>("");
  const [moduleValuation, setModuleValuation] = useState<ValuationMethod | "">("");
  const [moduleSaving, setModuleSaving] = useState(false);
  const [moduleError, setModuleError] = useState<string>("");

  const [loanAmount, setLoanAmount] = useState<string>("");
  const [loanType, setLoanType] = useState<string>("");
  const [loanRepayment, setLoanRepayment] = useState<string>("");
  const [loanTerm, setLoanTerm] = useState<string>("");
  const [interestRatePa, setInterestRatePa] = useState<string>("");
  const [repaymentMethod, setRepaymentMethod] = useState<string>("");
  const [rateOfDiscounting, setRateOfDiscounting] = useState<string>("");
  const [sanctionDateIso, setSanctionDateIso] = useState<string>(""); // YYYY-MM-DD for DatePicker
  const [fairValue, setFairValue] = useState<string>("");
  const [customPrincipalRows, setCustomPrincipalRows] = useState<string[]>([]);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `BORROWINGS_INDAS_${normalizedSection}`;
  };

  const currentSectionName = getSectionName(procedureId);
  const isDebtMasterDataScreen = currentSectionName === "DashboardAgreementSummary";

  const isoToDdMmYyyy = (iso: string): string => {
    if (!iso) return "";
    const parts = iso.split("-");
    if (parts.length !== 3) return iso;
    const [yyyy, mm, dd] = parts;
    if (!yyyy || !mm || !dd) return iso;
    return `${dd}-${mm}-${yyyy}`;
  };

  const ddMmYyyyToIso = (dmy: string): string => {
    if (!dmy) return "";
    const parts = dmy.split("-");
    if (parts.length !== 3) return "";
    const [dd, mm, yyyy] = parts;
    if (!yyyy || !mm || !dd) return "";
    return `${yyyy}-${mm}-${dd}`;
  };

  const getRowKey = (row: DebtMasterRow): string => {
    const trimmed = (row.debtName || "").trim();
    if (trimmed) return trimmed;
    if (row.sourceKey && row.sourceKey.trim()) return row.sourceKey.trim();
    return row.id;
  };

  const uploadDebtMasterData = async (payload: any) => {
    const fileName = "Execution_DEBT_MasterData.json";
    const jsonString = JSON.stringify(payload, null, 2);
    const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

    const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
      fileContent: base64Content,
      fileName,
      referenceValue: "Execution DEBT Master Data",
      folderName: "juggernaut",
      fyYear: getFY_YEAR(),
      sectionList: ["Execution_DEBT_MasterData"],
    });

    if (!uploadResult || !uploadResult.success) {
      throw new Error(uploadResult?.error || "Failed to save DEBT master data");
    }
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(",")[1] ?? "";
        resolve(base64);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });

  const getSharePointWebUrl = (uploadResult: any): string | undefined => {
    return (
      uploadResult?.data?.webUrl ||
      uploadResult?.webUrl ||
      uploadResult?.url ||
      uploadResult?.fileUrl ||
      uploadResult?.data?.url
    );
  };

  const handleBack = () => {
    if (setActiveSection) {
      setActiveSection("borindas-substantive");
    } else if (onBack) {
      onBack();
    }
  };

  // If no procedureId or invalid procedureId, show nothing (should be handled by routing)
  if (!procedureId || !currentSectionName) {
    return null;
  }

  // Find the procedure for the current section
  const currentProcedure = procedures.find(p => p.sectionName === currentSectionName);
  if (!currentProcedure) {
    return null;
  }

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn("Failed to load company name, using fallback");
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load area prefix from Sections.json
  useEffect(() => {
    const loadAreaPrefix = async () => {
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (window as any).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            const sectionsData = result.data;
            if (sectionsData.Execution && sectionsData.Execution["Borrowings - Ind AS"]) {
              setAreaPrefix(sectionsData.Execution["Borrowings - Ind AS"]);
            } else if (sectionsData.Execution && sectionsData.Execution["Borrowings"]) {
              // Fallback to existing Borrowings prefix if Ind AS is not configured separately
              setAreaPrefix(sectionsData.Execution["Borrowings"]);
            }
          }
        }
      } catch (error) {
        console.warn("Failed to load area prefix from Sections.json, using default Bor:", error);
      }
    };
    loadAreaPrefix();
  }, []);

  // Load saved responses
  useEffect(() => {
    const loadSavedResponses = async () => {
      if (isDebtMasterDataScreen) {
        return;
      }
      if (!companyName || !areaPrefix) {
        setLoading(false);
        return;
      }

      try {
        const fileName = `Execution_${areaPrefix}_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);

        if (result?.success && result?.data?.substantiveProcedures) {
          const savedData = result.data.substantiveProcedures[currentProcedure.name];
          if (savedData && Array.isArray(savedData.responses)) {
            setResponses(savedData.responses.map((r: any, index: number) => ({
              id: r.id || `response-${index}`,
              textResponse: r.textResponse || null,
              documents: (r.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            })));
          } else if (savedData) {
            // Legacy format - single response
            setResponses([{
              id: "response-0",
              textResponse: savedData.textResponse || null,
              documents: (savedData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            }]);
          }
          
          // Load inference question
          const inferenceQuestionId = `${currentProcedure.sectionName}-INFERENCE`;
          const inferenceData = result.data.substantiveProcedures[inferenceQuestionId];
          if (inferenceData) {
            setInferenceQuestion({
              id: "inference-conclusion",
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.error("Failed to load saved responses:", error);
      } finally {
        setLoading(false);
      }
    };

    loadSavedResponses();
  }, [companyName, areaPrefix, currentProcedure.name, isDebtMasterDataScreen]);

  // Load DEBT master data for "Dashboard & Agreement Summary"
  useEffect(() => {
    const loadDebtMasterData = async () => {
      if (!isDebtMasterDataScreen) return;
      setLoading(true);
      try {
        const fileName = "Execution_DEBT_MasterData.json";
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        const data = result?.success ? result?.data : {};
        const loansObj = data?.loans && typeof data.loans === "object" ? data.loans : {};
        const nextDetails: Record<string, any> = {};
        const rows: DebtMasterRow[] = Object.entries(loansObj).map(([debtNameKey, loanVal]: any, idx: number) => {
          const dashboard = loanVal?.dashboard ?? {};
          const { dashboard: _dash, ...rest } = (loanVal && typeof loanVal === "object") ? loanVal : {};
          nextDetails[String(debtNameKey)] = rest;
          return {
            id: String(dashboard?.id ?? `debt-${Date.now()}-${idx}`),
            debtName: String(dashboard?.debtName ?? debtNameKey ?? ""),
            partyName: String(dashboard?.partyName ?? ""),
            debtType: (dashboard?.debtType ?? "") as DebtMasterRow["debtType"],
            interestRate: (dashboard?.interestRate ?? "") as DebtMasterRow["interestRate"],
            agreementWpName: String(dashboard?.agreementWpName ?? ""),
            agreementWpUrl: undefined,
            valuationMethod: (dashboard?.valuationMethod ?? "") as DebtMasterRow["valuationMethod"],
            testingReference: String(dashboard?.testingReference ?? "") as DebtMasterRow["testingReference"],
            assessmentFileName: dashboard?.assessmentFileName ? String(dashboard.assessmentFileName) : undefined,
            assessmentFileUrl: dashboard?.assessmentFileUrl ? String(dashboard.assessmentFileUrl) : undefined,
            sourceKey: String(debtNameKey),
          };
        });
        setDebtRows(rows);
        setLoanDetailsByKey(nextDetails);
      } catch (error) {
        console.error("Failed to load Execution_DEBT_MasterData.json:", error);
        setDebtRows([]);
        setLoanDetailsByKey({});
      } finally {
        setLoading(false);
      }
    };
    loadDebtMasterData();
  }, [isDebtMasterDataScreen]);

  // Load list of available working papers for Agreement linking
  useEffect(() => {
    const loadAgreements = async () => {
      if (!isDebtMasterDataScreen) return;
      try {
        // Borrowings (and Borrowings - Ind AS) uses "DEBT" prefix for section codes in this app.
        const res = await (window as any)?.sharePointAPI?.loadDocumentListBySection?.("Execution_DEBT");
        if (res?.success && Array.isArray(res.data)) {
          setAvailableAgreements(
            res.data
              .map((d: any) => ({ name: String(d?.name ?? ""), url: d?.url ? String(d.url) : undefined }))
              .filter((d: any) => d.name && d.name.trim().length > 0)
          );
        } else {
          setAvailableAgreements([]);
        }
      } catch {
        setAvailableAgreements([]);
      }
    };
    loadAgreements();
  }, [isDebtMasterDataScreen]);

  // Keep custom repayment rows in sync with Loan Term (uneditable row count)
  useEffect(() => {
    if (!moduleOpen) return;
    const isCustom = (repaymentMethod || "").trim().toLowerCase() === "custom repayment schedule";
    const term = loanTerm.trim() === "" ? 0 : Number(loanTerm);
    if (!isCustom || !Number.isFinite(term) || term <= 0) {
      setCustomPrincipalRows([]);
      return;
    }
    setCustomPrincipalRows((prev) => {
      if (prev.length === term) return prev;
      const next = Array.from({ length: term }, (_, i) => prev[i] ?? "");
      return next;
    });
  }, [moduleOpen, repaymentMethod, loanTerm]);

  const handleAddResponse = () => {
    const newResponse: ProcedureResponse = {
      id: `response-${Date.now()}`,
      textResponse: null,
      documents: [],
    };
    setResponses([...responses, newResponse]);
  };

  const handleRemoveResponse = (id: string) => {
    setResponses(responses.filter(r => r.id !== id));
  };

  const handleTextResponseChange = (id: string, textResponse: { html: string; plainText: string } | null) => {
    setResponses(responses.map(r => 
      r.id === id ? { ...r, textResponse } : r
    ));
  };

  const handleDocumentsChange = (
    id: string,
    documents: Array<{ id: string; type: "textbox" | "linked" | "uploaded"; name: string; reference?: string; content?: string; url?: string }>
  ) => {
    setResponses(responses.map(r => 
      r.id === id ? { ...r, documents } : r
    ));
  };

  const handleSave = useCallback(async () => {
    if (!companyName || !areaPrefix) {
      setSaveError("Missing required data (company name or area prefix)");
      setSaveStatus('error');
      return;
    }

    // Auto-save any pending textbox content before processing responses
    const documentManagers = document.querySelectorAll('[data-document-manager]');
    for (const manager of Array.from(documentManagers)) {
      const itemId = manager.getAttribute('data-item-id');
      if (itemId) {
        const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
        if (autoSaveMethod && typeof autoSaveMethod === 'function') {
          autoSaveMethod();
        }
      }
    }

    // Wait for auto-save to complete
    await new Promise(resolve => setTimeout(resolve, 1000));

    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const fileName = `Execution_${areaPrefix}_Responses.json`;

      // Load existing file first
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Auto-save inference question textbox
      const inferenceItemId = `${currentProcedure.sectionName}-INFERENCE`;
      const inferenceAutoSave = (window as any)[`autoSaveTextbox_${inferenceItemId}`];
      if (inferenceAutoSave && typeof inferenceAutoSave === 'function') {
        inferenceAutoSave();
      }
      await new Promise(resolve => setTimeout(resolve, 500));

      // Save the responses
      const responsesToSave = responses.map(r => ({
        id: r.id,
        textResponse: r.textResponse || null,
        documents: (r.documents || []).map((d) => d.name || d),
      }));

      existingData.substantiveProcedures[currentProcedure.name] = {
        responses: responsesToSave,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceQuestionId = `${currentProcedure.sectionName}-INFERENCE`;
      const sectionToFind = `Execution_${areaPrefix}_${inferenceQuestionId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceItemId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponse = inferenceQuestion.textResponse || null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponse = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      }

      existingData.substantiveProcedures[inferenceQuestionId] = {
        selectedOption: "",
        textResponse: inferenceTextResponse,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      // Upload to SharePoint
      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: getFY_YEAR(),
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save responses");
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save responses:", error);
      setSaveStatus('error');
      setSaveError(error instanceof Error ? error.message : "Failed to save responses");
    } finally {
      setSaving(false);
    }
  }, [companyName, areaPrefix, responses, currentProcedure.name, inferenceQuestion, currentProcedure.sectionName]);

  const handleAddDebtRow = () => {
    const newRow: DebtMasterRow = {
      id: `debt-${Date.now()}`,
      debtName: "",
      partyName: "",
      debtType: "",
      interestRate: "",
      agreementWpName: "",
      agreementWpUrl: undefined,
      valuationMethod: "",
      testingReference: "",
    };
    setDebtRows((prev) => [...prev, newRow]);
  };

  const handleRemoveDebtRow = (id: string) => {
    setDebtRows((prev) => prev.filter((r) => r.id !== id));
  };

  const updateDebtRow = (id: string, patch: Partial<DebtMasterRow>) => {
    setDebtRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const buildDebtMasterPayload = (rows: DebtMasterRow[]) => {
    const loans: Record<string, any> = {};

    for (const row of rows) {
      const key = getRowKey(row);
      const details = loanDetailsByKey[key] ?? loanDetailsByKey[row.sourceKey || ""] ?? {};

      const dashboard: Record<string, any> = {
        id: row.id,
        debtName: row.debtName,
        partyName: row.partyName,
        debtType: row.debtType,
        interestRate: row.interestRate,
        agreementWpName: row.agreementWpName,
        valuationMethod: row.valuationMethod,
        testingReference: row.testingReference ?? "",
      };

      if (row.assessmentFileName) dashboard.assessmentFileName = row.assessmentFileName;
      if (row.assessmentFileUrl) dashboard.assessmentFileUrl = row.assessmentFileUrl;

      loans[key] = {
        ...(details && typeof details === "object" ? details : {}),
        dashboard,
      };
    }

    return { loans };
  };

  const handleSaveDebtMasterData = async () => {
    setDebtSaving(true);
    setDebtSaveStatus("saving");
    setDebtSaveError(null);
    try {
      const payload = buildDebtMasterPayload(debtRows);
      await uploadDebtMasterData(payload);

      setDebtSaveStatus("saved");
      setTimeout(() => setDebtSaveStatus("idle"), 2500);
    } catch (error) {
      console.error("Failed to save Execution_DEBT_MasterData.json:", error);
      setDebtSaveStatus("error");
      setDebtSaveError(error instanceof Error ? error.message : "Failed to save DEBT master data");
    } finally {
      setDebtSaving(false);
    }
  };

  const openLoanModuleDialog = (row: DebtMasterRow) => {
    const key = getRowKey(row);
    const existing = loanDetailsByKey[key] ?? loanDetailsByKey[row.sourceKey || ""] ?? {};

    setModuleLoanKey(key);
    setModuleRowId(row.id);
    setModuleValuation(row.valuationMethod);
    setModuleError("");

    setLoanAmount(existing?.loan_amount != null ? String(existing.loan_amount) : "");
    setLoanType(existing?.loan_type != null ? String(existing.loan_type) : "");
    setLoanRepayment(existing?.loan_repayment != null ? String(existing.loan_repayment) : "");
    setLoanTerm(existing?.loan_term != null ? String(existing.loan_term) : "");
    setInterestRatePa(existing?.interest_rate_pa != null ? String(existing.interest_rate_pa) : "");
    setRepaymentMethod(existing?.repayment_method != null ? String(existing.repayment_method) : "");
    setRateOfDiscounting(existing?.rate_of_discounting != null ? String(existing.rate_of_discounting) : "");
    setSanctionDateIso(existing?.sanction_date ? ddMmYyyyToIso(String(existing.sanction_date)) : "");
    setFairValue(existing?.fair_value != null ? String(existing.fair_value) : "");

    const term = Number(existing?.loan_term ?? 0);
    const customObj =
      existing?.custom_principal && typeof existing.custom_principal === "object"
        ? existing.custom_principal
        : null;

    if (term > 0 && customObj) {
      const next = Array.from({ length: term }, (_, i) => {
        const k = String(i + 1);
        return customObj[k] != null ? String(customObj[k]) : "";
      });
      setCustomPrincipalRows(next);
    } else {
      setCustomPrincipalRows([]);
    }

    setModuleOpen(true);
  };

  const handleSaveLoanModule = async () => {
    setModuleSaving(true);
    setModuleError("");
    try {
      if (!moduleLoanKey) throw new Error("Missing loan key");

      const parsedTerm = loanTerm.trim() === "" ? 0 : Number(loanTerm);
      const rateDisabled = (loanType || "").trim().toLowerCase() === "general";
      const isCustom = (repaymentMethod || "").trim().toLowerCase() === "custom repayment schedule";

      let custom_principal: Record<string, number> | undefined;
      if (isCustom && parsedTerm > 0) {
        const rows =
          customPrincipalRows.length === parsedTerm
            ? customPrincipalRows
            : Array.from({ length: parsedTerm }, (_, i) => customPrincipalRows[i] ?? "");
        const obj: Record<string, number> = {};
        rows.forEach((val, idx) => {
          const trimmed = (val ?? "").toString().trim();
          if (trimmed.length === 0) return;
          const num = Number(trimmed);
          if (!Number.isFinite(num)) return;
          obj[String(idx + 1)] = num;
        });
        custom_principal = obj;
      }

      const nextDetails: Record<string, any> = {};
      if (loanAmount.trim() !== "") nextDetails.loan_amount = Number(loanAmount);
      if (loanType) nextDetails.loan_type = loanType;
      if (loanRepayment) nextDetails.loan_repayment = loanRepayment;
      if (loanTerm.trim() !== "") nextDetails.loan_term = Number(loanTerm);
      if (interestRatePa.trim() !== "") nextDetails.interest_rate_pa = Number(interestRatePa);
      if (repaymentMethod) nextDetails.repayment_method = repaymentMethod;
      if (!rateDisabled && rateOfDiscounting.trim() !== "") nextDetails.rate_of_discounting = Number(rateOfDiscounting);
      if (sanctionDateIso) nextDetails.sanction_date = isoToDdMmYyyy(sanctionDateIso);
      if (moduleValuation === "FVTPL" && fairValue.trim() !== "") nextDetails.fair_value = Number(fairValue);
      if (custom_principal) nextDetails.custom_principal = custom_principal;

      // Persist into state
      setLoanDetailsByKey((prev) => ({ ...prev, [moduleLoanKey]: nextDetails }));

      // Upload merged JSON immediately
      const loans: Record<string, any> = {};
      for (const row of debtRows) {
        const key = getRowKey(row);
        const details =
          key === moduleLoanKey
            ? nextDetails
            : (loanDetailsByKey[key] ?? loanDetailsByKey[row.sourceKey || ""] ?? {});
        loans[key] = {
          ...(details && typeof details === "object" ? details : {}),
          dashboard: {
            id: row.id,
            debtName: row.debtName,
            partyName: row.partyName,
            debtType: row.debtType,
            interestRate: row.interestRate,
            agreementWpName: row.agreementWpName,
            valuationMethod: row.valuationMethod,
            testingReference: row.testingReference ?? "",
            assessmentFileName: row.assessmentFileName,
            assessmentFileUrl: row.assessmentFileUrl,
          },
        };
      }
      await uploadDebtMasterData({ loans });

      setModuleOpen(false);
    } catch (e: any) {
      setModuleError(e?.message || "Failed to save module");
    } finally {
      setModuleSaving(false);
    }
  };

  const persistDebtMasterAssessment = async (nextRows: DebtMasterRow[]) => {
    const payload = buildDebtMasterPayload(nextRows);
    await uploadDebtMasterData(payload);
  };

  const handleAssessmentFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const targetRowId = assessmentUploadTargetRowId;

    if (!file || !targetRowId) return;

    const row = debtRows.find((r) => r.id === targetRowId);
    if (!row) return;

    setAssessmentUploadingRowId(targetRowId);
    try {
      const fyYear = getFY_YEAR();
      if (!fyYear) throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");

      const base64Content = await fileToBase64(file);

      const referenceValue = `Execution_Debt_Assessment_${row.debtName || row.id}`;
      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: file.name,
        referenceValue,
        folderName: "client",
        fyYear,
        sectionList: ["Execution_DEBT_File"],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to upload assessment file");
      }

      // Prefer URL returned by uploadFile; fall back to querying section if needed.
      let webUrl = getSharePointWebUrl(uploadResult);
      if (!webUrl && (window as any).sharePointAPI?.loadDocumentListBySection) {
        try {
          const res = await (window as any).sharePointAPI.loadDocumentListBySection("Execution_DEBT_File");
          const match = Array.isArray(res?.data)
            ? res.data.find(
                (f: any) =>
                  f?.name === file.name && (f?.reference === referenceValue || !f?.reference)
              )
            : undefined;
          webUrl = match?.url || match?.webUrl || match?.data?.webUrl;
        } catch {
          // Non-fatal: allow saving even if URL isn't available.
        }
      }

      const nextRows = debtRows.map((r) =>
        r.id === targetRowId ? { ...r, assessmentFileName: file.name, assessmentFileUrl: webUrl } : r
      );
      setDebtRows(nextRows);
      await persistDebtMasterAssessment(nextRows);
    } finally {
      setAssessmentUploadingRowId(null);
      setAssessmentUploadTargetRowId(null);
      if (assessmentFileInputRef.current) assessmentFileInputRef.current.value = "";
    }
  };

  const handleAssessmentUploadClick = (row: DebtMasterRow) => {
    setAssessmentUploadTargetRowId(row.id);
    assessmentFileInputRef.current?.click();
  };

  const handleDeleteAssessment = async (row: DebtMasterRow) => {
    if (!row.assessmentFileName && !row.assessmentFileUrl) return;

    setAssessmentDeletingRowId(row.id);
    try {
      const nextRows = debtRows.map((r) =>
        r.id === row.id
          ? { ...r, assessmentFileName: undefined, assessmentFileUrl: undefined }
          : r
      );
      setDebtRows(nextRows);
      await persistDebtMasterAssessment(nextRows);
    } finally {
      setAssessmentDeletingRowId(null);
    }
  };

  const openAssessmentUrlInBrowser = async (url: string | undefined) => {
    if (!url) return;
    if ((window as any).electronWindow?.openExternal) {
      try {
        await (window as any).electronWindow.openExternal(url);
      } catch (error) {
        console.error("Error opening external URL:", error);
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-white/60">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">{currentProcedure.name}</h2>
          <p className="text-gray-400">Document procedures for this substantive procedure</p>
        </div>
        <div className="flex items-center gap-2">
          {isDebtMasterDataScreen && (
            <Button
              onClick={handleSaveDebtMasterData}
              disabled={debtSaving}
              className="bg-[#4da3ff] text-black hover:bg-[#4da3ff]/90"
            >
              {debtSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          )}
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      {isDebtMasterDataScreen ? (
        <>
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-1">
                <CardTitle className="text-white">DEBT Master Data</CardTitle>
                <p className="text-sm text-white/60 mt-1">
                  Enter debt agreements here. This table saves to <span className="font-mono">Execution_DEBT_MasterData.json</span>.
                </p>
                {debtTestError && (
                  <p className="text-xs text-red-400">
                    {debtTestError}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleAddDebtRow}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add row
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      setDebtTestRunning(true);
                      setDebtTestError(null);
                      const payrollApi = (window as any).payroll;
                      if (!payrollApi?.run) {
                        throw new Error("Run Test is not available in this environment");
                      }
                      const res = await payrollApi.run("execute_debt_dashboard", {
                        inputFiles: [],
                        options: {},
                      });
                      if (!res?.ok) {
                        const msg = res?.error || "Failed to run test";
                        if (msg.includes("Atleast fill details of one Loan")) {
                          setDebtTestError("Atleast fill details of one Loan");
                        }
                      }
                    } catch (e: any) {
                      const msg = e?.message || String(e || "");
                      if (msg.includes("Atleast fill details of one Loan")) {
                        setDebtTestError("Atleast fill details of one Loan");
                      }
                    } finally {
                      setDebtTestRunning(false);
                    }
                  }}
                  variant="outline"
                  size="sm"
                  disabled={debtTestRunning}
                  className="border-emerald-400/50 text-emerald-300 hover:bg-emerald-500/10"
                >
                  {debtTestRunning ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  Run Test
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {(debtSaveStatus === "error" || debtSaveStatus === "saved") && (
              <div
                className={[
                  "rounded-md border px-3 py-2 text-sm",
                  debtSaveStatus === "saved"
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                    : "border-red-500/30 bg-red-500/10 text-red-200",
                ].join(" ")}
              >
                {debtSaveStatus === "saved"
                  ? "Saved successfully."
                  : debtSaveError || "Save failed."}
              </div>
            )}

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Debt Name</TableHead>
                  <TableHead>Party Name</TableHead>
                  <TableHead>Debt type</TableHead>
                  <TableHead>Interest Rate</TableHead>
                  <TableHead>Agreement</TableHead>
                  <TableHead>Valuation Method</TableHead>
                  <TableHead>Assessment</TableHead>
                  <TableHead>Testing Reference</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {debtRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-white/60 py-6">
                      No rows yet. Click “Add row” to start.
                    </TableCell>
                  </TableRow>
                ) : (
                  debtRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="min-w-[220px]">
                        <Input
                          value={row.debtName}
                          onChange={(e) => updateDebtRow(row.id, { debtName: e.target.value })}
                          placeholder="Entered by user"
                          className="bg-black/30 border-white/10 text-white placeholder:text-white/40"
                        />
                      </TableCell>
                      <TableCell className="min-w-[220px]">
                        <Input
                          value={row.partyName}
                          onChange={(e) => updateDebtRow(row.id, { partyName: e.target.value })}
                          placeholder="Entered by user"
                          className="bg-black/30 border-white/10 text-white placeholder:text-white/40"
                        />
                      </TableCell>
                      <TableCell className="min-w-[190px]">
                        <Select
                          value={row.debtType}
                          onValueChange={(v) => updateDebtRow(row.id, { debtType: v as DebtMasterRow["debtType"] })}
                          searchable={false}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent searchable={false}>
                            <SelectItem value="Debenture">Debenture</SelectItem>
                            <SelectItem value="Bonds">Bonds</SelectItem>
                            <SelectItem value="Term Loan">Term Loan</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="min-w-[170px]">
                        <Select
                          value={row.interestRate}
                          onValueChange={(v) => updateDebtRow(row.id, { interestRate: v as DebtMasterRow["interestRate"] })}
                          searchable={false}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent searchable={false}>
                            <SelectItem value="Fixed">Fixed</SelectItem>
                            <SelectItem value="Floating">Floating</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="min-w-[240px]">
                        <Select
                          value={row.agreementWpName || "__none__"}
                          onValueChange={(v) => {
                            if (v === "__none__") {
                              updateDebtRow(row.id, { agreementWpName: "", agreementWpUrl: undefined });
                              return;
                            }
                            const found = availableAgreements.find((a) => a.name === v);
                            updateDebtRow(row.id, { agreementWpName: v, agreementWpUrl: found?.url });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Link existing WP" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            {availableAgreements.map((a) => (
                              <SelectItem key={a.name} value={a.name}>
                                {a.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="min-w-[190px]">
                        <Select
                          value={row.valuationMethod}
                          onValueChange={(v) => updateDebtRow(row.id, { valuationMethod: v as DebtMasterRow["valuationMethod"] })}
                          searchable={false}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent searchable={false}>
                            <SelectItem value="Amortised Cost">Amortised Cost</SelectItem>
                            <SelectItem value="FVTPL">FVTPL</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="min-w-[240px]">
                        {row.assessmentFileUrl ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="text-xs text-blue-300 hover:underline underline-offset-2 truncate max-w-[165px]"
                              onClick={() => openAssessmentUrlInBrowser(row.assessmentFileUrl)}
                              title={row.assessmentFileName || "Open assessment file"}
                            >
                              {row.assessmentFileName || "Open file"}
                            </button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAssessment(row)}
                              disabled={assessmentDeletingRowId === row.id}
                              className="text-red-400 hover:text-red-300"
                              title="Remove from dashboard (does not delete from SharePoint)"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-white/20 text-white hover:bg-white/10"
                            onClick={() => handleAssessmentUploadClick(row)}
                            disabled={!!assessmentUploadingRowId}
                            title="Upload assessment file"
                          >
                            {assessmentUploadingRowId === row.id ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload
                              </>
                            )}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="min-w-[260px]">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-white/20 text-white hover:bg-white/10"
                          onClick={() => {
                            openLoanModuleDialog(row);
                          }}
                          data-allow-archival-edit={true}
                        >
                          Open Module
                        </Button>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          onClick={() => handleRemoveDebtRow(row.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <input
          ref={assessmentFileInputRef}
          type="file"
          accept="*"
          className="hidden"
          onChange={handleAssessmentFileSelected}
        />

        <Dialog
          open={moduleOpen}
          onOpenChange={(open) => {
            if (!open && !moduleSaving) {
              setModuleOpen(false);
              setModuleError("");
            }
          }}
        >
          <DialogContent className="sm:max-w-[980px] max-h-[85vh] overflow-y-auto bg-black/90 border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>
                {moduleValuation === "FVTPL" ? "FVTPL Module" : "Amortised Cost Module"}{" "}
                <span className="text-white/60 text-sm font-normal">({moduleLoanKey || moduleRowId})</span>
              </DialogTitle>
              <DialogDescription className="text-white/60">
                Fill the loan inputs below. Saving will update{" "}
                <span className="font-mono">Execution_DEBT_MasterData.json</span>.
              </DialogDescription>
            </DialogHeader>

            {moduleError && (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                {moduleError}
              </div>
            )}

            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-white">Loan Amount (net of transaction costs if any)</Label>
                  <Input
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(e.target.value)}
                    placeholder="Entered by user"
                    className="bg-black/30 border-white/10 text-white placeholder:text-white/40"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Loan Type</Label>
                  <Select value={loanType} onValueChange={setLoanType} searchable={false}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent searchable={false}>
                      <SelectItem value="Embedded Derivative">Embedded Derivative</SelectItem>
                      <SelectItem value="General">General</SelectItem>
                      <SelectItem value="Interest Free">Interest Free</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Loan Repayment</Label>
                  <Select value={loanRepayment} onValueChange={setLoanRepayment} searchable={false}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent searchable={false}>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                      <SelectItem value="Annually">Annually</SelectItem>
                      <SelectItem value="Semi-Annually">Semi-Annually</SelectItem>
                      <SelectItem value="Quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Loan Term</Label>
                  <Input
                    value={loanTerm}
                    onChange={(e) => setLoanTerm(e.target.value)}
                    placeholder="Enter no. of periods"
                    className="bg-black/30 border-white/10 text-white placeholder:text-white/40"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Interest Rate p.a.</Label>
                  <Input
                    value={interestRatePa}
                    onChange={(e) => setInterestRatePa(e.target.value)}
                    placeholder="Entered by user"
                    className="bg-black/30 border-white/10 text-white placeholder:text-white/40"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Repayment Method</Label>
                  <Select value={repaymentMethod} onValueChange={setRepaymentMethod} searchable={false}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent searchable={false}>
                      {(loanType || "").trim().toLowerCase() === "general" && (
                        <SelectItem value="Fixed Annuity">Fixed Annuity</SelectItem>
                      )}
                      <SelectItem value="Equal Principal Installments">Equal Principal Installments</SelectItem>
                      <SelectItem value="Custom Repayment Schedule">Custom Repayment Schedule</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Rate of discounting</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={rateOfDiscounting}
                      onChange={(e) => setRateOfDiscounting(e.target.value)}
                      placeholder="0 if no discounting"
                      disabled={(loanType || "").trim().toLowerCase() === "general"}
                      className="bg-black/30 border-white/10 text-white placeholder:text-white/40 disabled:opacity-50"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                      onClick={() => setRateOfDiscounting("0")}
                      disabled={(loanType || "").trim().toLowerCase() === "general"}
                      title="Set 0 if no discounting required"
                    >
                      0
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Sanction Date</Label>
                  <Input
                    type="date"
                    value={sanctionDateIso}
                    onChange={(e) => setSanctionDateIso(e.target.value)}
                    className="bg-black/40 border-white/10 text-white"
                  />
                </div>

                {moduleValuation === "FVTPL" && (
                  <div className="space-y-2">
                    <Label className="text-white">Fair Value</Label>
                    <Input
                      value={fairValue}
                      onChange={(e) => setFairValue(e.target.value)}
                      placeholder="Entered by user"
                      className="bg-black/30 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                )}
              </div>

              {(repaymentMethod || "").trim().toLowerCase() === "custom repayment schedule" && (
                <Card className="border-white/10 bg-black/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white text-base">Custom Repayment Schedule</CardTitle>
                    <p className="text-sm text-white/60">
                      Installment rows are fixed to the Loan Term (uneditable count). Principal repayment is optional.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[320px] overflow-y-auto pr-1">
                      <Table>
                        <TableHeader className="sticky top-0 bg-black/60 backdrop-blur border-b border-white/10">
                          <TableRow>
                            <TableHead>Installment</TableHead>
                            <TableHead>Principal Repayment</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(customPrincipalRows.length ? customPrincipalRows : []).map((val, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-white/80">{idx + 1}</TableCell>
                              <TableCell>
                                <Input
                                  value={val}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setCustomPrincipalRows((prev) => prev.map((p, i) => (i === idx ? v : p)));
                                  }}
                                  placeholder="Not compulsory"
                                  className="bg-black/30 border-white/10 text-white placeholder:text-white/40"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                          {customPrincipalRows.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={2} className="text-white/60 py-4">
                                Enter Loan Term above to generate rows.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
                onClick={() => setModuleOpen(false)}
                disabled={moduleSaving}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSaveLoanModule}
                disabled={moduleSaving}
                className="bg-[#4da3ff] text-black hover:bg-[#4da3ff]/90"
              >
                {moduleSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </>
      ) : (
        <>
          <Card className="border-white/10 bg-black/40">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">{currentProcedure.name}</CardTitle>
                <Button
                  onClick={handleAddResponse}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {responses.length === 0 ? (
                <div className="text-center py-8 text-white/60">
                  <p>No responses yet. Click "Add" to start documenting this procedure.</p>
                </div>
              ) : (
                responses.map((response, index) => (
                  <Card key={response.id} className="border-white/10 bg-black/20 mb-4">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-white">
                          {currentProcedure.name} {responses.length > 1 ? `(${index + 1})` : ""}
                        </Label>
                        {responses.length > 1 && (
                          <Button
                            onClick={() => handleRemoveResponse(response.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <DocumentManager
                        sectionKey={`execution-substantive:Borrowings - Ind AS`}
                        itemId={`${currentProcedure.sectionName}-${response.id}`}
                        itemQuestion={currentProcedure.name}
                        itemTextResponse={response.textResponse || null}
                        itemDocuments={response.documents || []}
                        onDocumentsChange={(docs) => handleDocumentsChange(response.id, docs)}
                        onTextResponseChange={(textResp) => handleTextResponseChange(response.id, textResp)}
                        disabled={false}
                      />
                    </CardContent>
                  </Card>
                ))
              )}
            </CardContent>
          </Card>

          {/* Inference and Conclusion Question */}
          <Card className="border-white/10 bg-black/40">
            <CardHeader>
              <CardTitle className="text-white">
                Document the inference and conclusion from the above procedure performed.
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DocumentManager
                sectionKey={`execution-substantive:Borrowings - Ind AS`}
                itemId={`${currentProcedure.sectionName}-INFERENCE`}
                itemQuestion="Document the inference and conclusion from the above procedure performed."
                itemTextResponse={inferenceQuestion.textResponse || null}
                itemDocuments={inferenceQuestion.documents || []}
                onDocumentsChange={(docs) => setInferenceQuestion({ ...inferenceQuestion, documents: docs })}
                onTextResponseChange={(textResp) => setInferenceQuestion({ ...inferenceQuestion, textResponse: textResp })}
                disabled={false}
              />
            </CardContent>
          </Card>
        </>
      )}

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {};
            procedures.forEach((proc) => {
              sectionMap[getSectionQuestionId(proc.name)] = proc.name;
            });
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}

